import { FormControl } from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { frFR } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import { useEffect } from 'react';

// FIXME: to be managed in the main app to sync with translations
dayjs.locale('fr');

const defaultPredefinedRanges: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: 'Cette semaine',
    getValue: () => {
      const today = dayjs();
      return [today.startOf('week'), today.endOf('week')];
    },
  },
  {
    label: 'Dernière semaine',
    getValue: () => {
      const today = dayjs();
      const prevWeek = today.subtract(7, 'day');
      return [prevWeek.startOf('week'), prevWeek.endOf('week')];
    },
  },
  {
    label: 'Derniers 7 jours',
    getValue: () => {
      const today = dayjs();
      return [today.subtract(7, 'day'), today];
    },
  },
  {
    label: 'Ce mois-ci',
    getValue: () => {
      const today = dayjs();
      return [today.startOf('month'), today.endOf('month')];
    },
  },
  {
    label: 'Prochain mois',
    getValue: () => {
      const today = dayjs();
      const startOfNextMonth = today.endOf('month').add(1, 'day');
      return [startOfNextMonth, startOfNextMonth.endOf('month')];
    },
  },
];

/**
 * Props for the DateTimeRangePicker component
 */
type DateTimeRangePickerComponentProps = {
  label?: string;
  defaultValues?: [Date | null, Date | null];
  predefinedRanges?: PickersShortcutsItem<DateRange<Dayjs>>[];
  format?: string;
  onChange: (newValue: [Date, Date] | null) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  safeDefault?: boolean;
};

/**
 * MUI Date Time Range Picker Component
 * @param label Optionnal label for default display
 * @param defaultValues Optionnal source dates from the database (in edit mode).
 * @param predefinedRanges Optionnal predefinedRanges for shortcuts
 * @param format Optionnal date format
 * @param onChange Callback function to handle date changes
 * @param required Whether the date range is required
 * @param error Whether the date range has an error state
 * @param helperText Optional helper text to display below the input field
 * @param safeDefault Optional. Determines whether to replace with a safe value
 *                    [new Date(), new Date()] if there are no defaultValues.
 *                    If false - the field still empty (null, null).
 * @returns
 */
function DateTimeRangePickerComponent({
  label = 'Choisir une période',
  defaultValues,
  predefinedRanges,
  format = 'DD/MM/YYYY HH:mm',
  onChange,
  required = true,
  error = false,
  helperText = '',
  safeDefault = true,
}: DateTimeRangePickerComponentProps) {
  // const safeDefault: [Date, Date] = defaultValues ?? [new Date(), new Date()];
  const resolvedDefaultValues: [Date | null, Date | null] =
    defaultValues && defaultValues.length === 2
      ? defaultValues
      : safeDefault
      ? [new Date(), new Date()]
      : [null, null];

  // auto-sync for safeDefault = true -> if we don't have default values, we assign current date to values
  useEffect(() => {
    if (safeDefault && resolvedDefaultValues[0] && resolvedDefaultValues[1]) {
      onChange([resolvedDefaultValues[0], resolvedDefaultValues[1]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeDefault]); // once, when we creating this component

  return (
    <FormControl fullWidth margin="normal">
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="fr"
        localeText={
          frFR.components.MuiLocalizationProvider.defaultProps.localeText
        }
      >
        <DateRangePicker
          label={required ? `${label} *` : label}
          defaultValue={
            // [dayjs(safeDefault[0]), dayjs(safeDefault[1])]
            resolvedDefaultValues[0] && resolvedDefaultValues[1]
              ? [
                  dayjs(resolvedDefaultValues[0]),
                  dayjs(resolvedDefaultValues[1]),
                ]
              : [null, null]
          }
          format={format}
          // defaultValue={[dayjs(safeDefault[0]), dayjs(safeDefault[1])]}
          onChange={(newValue) => {
            if (newValue && newValue[0] && newValue[1]) {
              onChange([newValue[0].toDate(), newValue[1].toDate()]);
            } else {
              onChange(null);
            }
          }}
          slotProps={{
            shortcuts: {
              items:
                predefinedRanges && predefinedRanges.length > 0
                  ? predefinedRanges
                  : defaultPredefinedRanges,
            },
            textField: {
              error,
              helperText: error ? helperText : '',
              InputLabelProps: {
                shrink: true,
              },
            },
            field: { clearable: true },
          }}
        />
      </LocalizationProvider>
    </FormControl>
  );
}

export default DateTimeRangePickerComponent;
